import { gql } from '@apollo/client';

/**
 * Spec 019 (services site), Provider side: profile, workers, prices,
 * credentials, worker logins, tax info and the admin credential queue. Every
 * Provider read and write returns `ProviderWorker`, so the private settings
 * never ride on `Car`.
 */

/** Everything the worker wizard, manage page and price page read. */
export const PROVIDER_WORKER_FIELDS = gql`
  fragment ProviderWorkerFields on ProviderWorker {
    carId
    car {
      id
      name
      status
      isCreated
      priceCalendar {
        date
        price
        isAvailable
      }
      location {
        id
        name
        city
        state
        zipCode
        timezone
      }
    }
    worker {
      carId
      displayName
      bio
      city
      state
      zip
      serviceRadiusMiles
      distanceUnit
      acceptsOutOfRadius
      perMileBeyondRadius
      maxTravelMiles
      instantBook
      bufferHours
      advanceNoticeHours
      offerings {
        id
        serviceType
        pricingMode
        priceTable
        hourlyRate
        minimumBillableMinutes
        addOns {
          code
          label
          price
          unit
          minutes
        }
        isActive
      }
      suppliesProvided {
        cleaningProducts
        tools
        vacuum
        linens
        guestConsumables
      }
      equipmentPhotos
      backgroundCheckVerified
      onTimeRate
      cancellationRate
      completedJobCount
      paused
      averageRating
      totalRatings
      languages
      yearsExperience
    }
    settings {
      listingStatus
      wizardStep
      serviceZips
      workingHours
      confirmWindowHours
      backgroundCheck {
        vendor
        completedAt
        expiresAt
        verified
      }
      pausedAt
      pauseReason
      suppliesSet
    }
    publishProblems {
      code
      field
      message
    }
    priceWarnings {
      field
      price
      estimatedMinutes
      effectiveHourly
      minimumWage
      wageSource
    }
  }
`;

export const SERVICE_PROVIDER_FIELDS = gql`
  fragment ServiceProviderFields on ServiceProvider {
    userId
    providerKind
    businessName
    insuranceVerified
    insuranceExpiresAt
    insurancePending
    feeWaivedUntil
    taxInfoComplete
    averageRating
    totalRatings
  }
`;

export const MY_SERVICE_PROVIDER = gql`
  query MyServiceProvider {
    myServiceProvider {
      ...ServiceProviderFields
    }
  }
  ${SERVICE_PROVIDER_FIELDS}
`;

export const UPSERT_SERVICE_PROVIDER = gql`
  mutation UpsertServiceProvider($input: ServiceProviderInput!) {
    upsertServiceProvider(input: $input) {
      ...ServiceProviderFields
    }
  }
  ${SERVICE_PROVIDER_FIELDS}
`;

export const SUBMIT_PROVIDER_INSURANCE = gql`
  mutation SubmitProviderInsurance($input: ProviderCredentialInput!) {
    submitProviderInsurance(input: $input) {
      ...ServiceProviderFields
    }
  }
  ${SERVICE_PROVIDER_FIELDS}
`;

export const MY_WORKERS = gql`
  query MyWorkers($locationId: Int, $status: ServiceListingStatus) {
    myWorkers(locationId: $locationId, status: $status) {
      ...ProviderWorkerFields
    }
  }
  ${PROVIDER_WORKER_FIELDS}
`;

export const MY_WORKER = gql`
  query MyWorker($workerCarId: ID!) {
    myWorker(workerCarId: $workerCarId) {
      ...ProviderWorkerFields
    }
  }
  ${PROVIDER_WORKER_FIELDS}
`;

export const WORKER_PUBLISH_CHECKLIST = gql`
  query WorkerPublishChecklist($workerCarId: ID!) {
    workerPublishChecklist(workerCarId: $workerCarId) {
      code
      field
      message
    }
  }
`;

export const MINIMUM_WAGE_FOR = gql`
  query MinimumWageFor($locationId: Int!) {
    minimumWageFor(locationId: $locationId) {
      hourlyRate
      source
      locationId
    }
  }
`;

export const CREATE_WORKER_DRAFT = gql`
  mutation CreateWorkerDraft($locationId: Int!, $displayName: String!) {
    createWorkerDraft(locationId: $locationId, displayName: $displayName) {
      ...ProviderWorkerFields
    }
  }
  ${PROVIDER_WORKER_FIELDS}
`;

export const UPDATE_WORKER = gql`
  mutation UpdateWorker($workerCarId: ID!, $input: WorkerSettingsInput!) {
    updateWorker(workerCarId: $workerCarId, input: $input) {
      ...ProviderWorkerFields
    }
  }
  ${PROVIDER_WORKER_FIELDS}
`;

export const COPY_WORKER_SETTINGS = gql`
  mutation CopyWorkerSettings($fromWorkerCarId: ID!, $toWorkerCarId: ID!) {
    copyWorkerSettings(fromWorkerCarId: $fromWorkerCarId, toWorkerCarId: $toWorkerCarId) {
      ...ProviderWorkerFields
    }
  }
  ${PROVIDER_WORKER_FIELDS}
`;

export const UPSERT_SERVICE_OFFERING = gql`
  mutation UpsertServiceOffering($workerCarId: ID!, $input: ServiceOfferingInput!) {
    upsertServiceOffering(workerCarId: $workerCarId, input: $input) {
      ...ProviderWorkerFields
    }
  }
  ${PROVIDER_WORKER_FIELDS}
`;

export const DELETE_SERVICE_OFFERING = gql`
  mutation DeleteServiceOffering($offeringId: ID!) {
    deleteServiceOffering(offeringId: $offeringId) {
      ...ProviderWorkerFields
    }
  }
  ${PROVIDER_WORKER_FIELDS}
`;

export const UPDATE_WORKER_EQUIPMENT_PHOTOS = gql`
  mutation UpdateWorkerEquipmentPhotos($workerCarId: ID!, $keys: [String!]!) {
    updateWorkerEquipmentPhotos(workerCarId: $workerCarId, keys: $keys) {
      ...ProviderWorkerFields
    }
  }
  ${PROVIDER_WORKER_FIELDS}
`;

export const SUBMIT_WORKER_BACKGROUND_CHECK = gql`
  mutation SubmitWorkerBackgroundCheck($workerCarId: ID!, $input: BackgroundCheckInput!) {
    submitWorkerBackgroundCheck(workerCarId: $workerCarId, input: $input) {
      ...ProviderWorkerFields
    }
  }
  ${PROVIDER_WORKER_FIELDS}
`;

export const PUBLISH_WORKER = gql`
  mutation PublishWorker($workerCarId: ID!) {
    publishWorker(workerCarId: $workerCarId) {
      ...ProviderWorkerFields
    }
  }
  ${PROVIDER_WORKER_FIELDS}
`;

export const UNLIST_WORKER = gql`
  mutation UnlistWorker($workerCarId: ID!) {
    unlistWorker(workerCarId: $workerCarId) {
      ...ProviderWorkerFields
    }
  }
  ${PROVIDER_WORKER_FIELDS}
`;

/** Private upload into `service-docs/<userId>/`; the key goes to a submit mutation. */
export const GET_PRESIGNED_URL_FOR_SERVICE_DOCUMENT = gql`
  mutation GetPresignedUrlForServiceDocument($contentType: String!, $contentLength: Int!) {
    getPresignedUrlForServiceDocument(contentType: $contentType, contentLength: $contentLength) {
      url
      key
    }
  }
`;

export const WORKER_ACCOUNT_FIELDS = gql`
  fragment WorkerAccountFields on WorkerAccount {
    id
    workerCarId
    status
    inviteEmail
    invitePhone
    invitedAt
    inviteExpiresAt
    acceptedAt
    userName
  }
`;

export const WORKER_ACCOUNTS = gql`
  query WorkerAccounts($workerCarId: ID!) {
    workerAccounts(workerCarId: $workerCarId) {
      ...WorkerAccountFields
    }
  }
  ${WORKER_ACCOUNT_FIELDS}
`;

/** `inviteUrl` is only in this answer: the Provider sends it (there is no SMS sender). */
export const INVITE_WORKER = gql`
  mutation InviteWorker($workerCarId: ID!, $email: String, $phone: String) {
    inviteWorker(workerCarId: $workerCarId, email: $email, phone: $phone) {
      ...WorkerAccountFields
      inviteUrl
    }
  }
  ${WORKER_ACCOUNT_FIELDS}
`;

export const ACCEPT_WORKER_INVITE = gql`
  mutation AcceptWorkerInvite($token: String!) {
    acceptWorkerInvite(token: $token) {
      ...WorkerAccountFields
    }
  }
  ${WORKER_ACCOUNT_FIELDS}
`;

export const REMOVE_WORKER_ACCOUNT = gql`
  mutation RemoveWorkerAccount($workerAccountId: ID!) {
    removeWorkerAccount(workerAccountId: $workerAccountId)
  }
`;

export const PROVIDER_TAX_INFO_FIELDS = gql`
  fragment ProviderTaxInfoFields on ProviderTaxInfoSummary {
    legalName
    classification
    tinType
    tinLast4
    signedAt
  }
`;

export const MY_PROVIDER_TAX_INFO = gql`
  query MyProviderTaxInfo {
    myProviderTaxInfo {
      ...ProviderTaxInfoFields
    }
  }
  ${PROVIDER_TAX_INFO_FIELDS}
`;

/** D28: the full TIN is write-only; the answer carries the last 4 digits. */
export const SUBMIT_PROVIDER_TAX_INFO = gql`
  mutation SubmitProviderTaxInfo($input: ProviderTaxInfoInput!) {
    submitProviderTaxInfo(input: $input) {
      ...ProviderTaxInfoFields
    }
  }
  ${PROVIDER_TAX_INFO_FIELDS}
`;

export const ADMIN_PENDING_SERVICE_CREDENTIALS = gql`
  query AdminPendingServiceCredentials {
    adminPendingServiceCredentials {
      kind
      providerId
      providerName
      workerCarId
      workerName
      issuer
      completedAt
      expiresAt
      coverageAmount
      fileKey
    }
  }
`;

export const VERIFY_PROVIDER_INSURANCE = gql`
  mutation VerifyProviderInsurance($providerId: ID!, $approved: Boolean!) {
    verifyProviderInsurance(providerId: $providerId, approved: $approved) {
      userId
      insuranceVerified
    }
  }
`;

export const VERIFY_WORKER_BACKGROUND_CHECK = gql`
  mutation VerifyWorkerBackgroundCheck(
    $workerCarId: ID!
    $approved: Boolean!
    $expiresAt: DateTime
  ) {
    verifyWorkerBackgroundCheck(
      workerCarId: $workerCarId
      approved: $approved
      expiresAt: $expiresAt
    ) {
      carId
      worker {
        carId
        backgroundCheckVerified
      }
    }
  }
`;

// ─── customer side (spec 019 US2, W10 to W16) ──────────────────────────────

/** A worker as customers see it: area only, never the location (SC-008). */
export const PUBLIC_WORKER_FIELDS = gql`
  fragment PublicWorkerFields on ServiceWorker {
    carId
    displayName
    bio
    city
    state
    zip
    areaCenter {
      lat
      lng
    }
    serviceRadiusMiles
    distanceUnit
    acceptsOutOfRadius
    perMileBeyondRadius
    maxTravelMiles
    instantBook
    bufferHours
    advanceNoticeHours
    offerings {
      id
      serviceType
      pricingMode
      priceTable
      hourlyRate
      minimumBillableMinutes
      addOns {
        code
        label
        price
        unit
        minutes
      }
      isActive
    }
    suppliesProvided {
      cleaningProducts
      tools
      vacuum
      linens
      guestConsumables
    }
    equipmentPhotos
    backgroundCheckVerified
    onTimeRate
    cancellationRate
    completedJobCount
    paused
    averageRating
    totalRatings
    languages
    yearsExperience
    provider {
      userId
      providerKind
      businessName
      insuranceVerified
      averageRating
      totalRatings
    }
  }
`;

export const SERVICE_QUOTE_FIELDS = gql`
  fragment ServiceQuoteFields on ServiceQuote {
    lines {
      label
      amount
      kind
    }
    serviceSubtotal
    addOnsSubtotal
    travelFee
    platformServiceFee
    serviceTax
    total
    addOnAllowance
    authorizedAmount
    isEstimate
    estimatedMinutes
  }
`;

export const SEARCH_WORKERS = gql`
  query SearchWorkers($input: SearchWorkersInput!) {
    searchWorkers(input: $input) {
      total
      page
      limit
      items {
        worker {
          ...PublicWorkerFields
        }
        car {
          id
          thumbnailUrl
          images {
            imageUrl
          }
        }
        distanceMiles
        rangeStatus
        travelFee
        bookable
        notBookableReason
        quote {
          ...ServiceQuoteFields
        }
        suppliesWarnings
      }
    }
  }
  ${PUBLIC_WORKER_FIELDS}
  ${SERVICE_QUOTE_FIELDS}
`;

export const SERVICE_WORKER = gql`
  query ServiceWorker($carId: ID!) {
    serviceWorker(carId: $carId) {
      id
      name
      thumbnailUrl
      images {
        imageUrl
      }
      location {
        city
        state
        zipCode
        timezone
      }
      serviceWorker {
        ...PublicWorkerFields
      }
    }
  }
  ${PUBLIC_WORKER_FIELDS}
`;

export const WORKER_QUOTE = gql`
  query WorkerQuote($workerCarId: ID!, $input: WorkerQuoteInput!) {
    workerQuote(workerCarId: $workerCarId, input: $input) {
      ...ServiceQuoteFields
    }
  }
  ${SERVICE_QUOTE_FIELDS}
`;

export const WORKER_AVAILABILITY = gql`
  query WorkerAvailability($workerCarId: ID!, $from: String!, $to: String!) {
    workerAvailability(workerCarId: $workerCarId, from: $from, to: $to) {
      startAt
      endAt
      kind
    }
  }
`;

export const SERVICE_SITE_FIELDS = gql`
  fragment ServiceSiteFields on ServiceSite {
    id
    kind
    nickname
    addressLine1
    addressLine2
    city
    state
    zip
    latitude
    longitude
    timezone
    homeDetails
    homeSupplies
    vehicleDetails
    hasAccessInstructions
    externalCalendarUrl
    calendarDefaults
    calendarLastSyncAt
    calendarLastError
    isActive
  }
`;

export const MY_SERVICE_SITES = gql`
  query MyServiceSites($includeInactive: Boolean) {
    myServiceSites(includeInactive: $includeInactive) {
      ...ServiceSiteFields
    }
  }
  ${SERVICE_SITE_FIELDS}
`;

export const SERVICE_SITE_ACCESS_INSTRUCTIONS = gql`
  query ServiceSiteAccessInstructions($siteId: ID!) {
    serviceSiteAccessInstructions(siteId: $siteId)
  }
`;

export const CREATE_SERVICE_SITE = gql`
  mutation CreateServiceSite($input: ServiceSiteInput!) {
    createServiceSite(input: $input) {
      ...ServiceSiteFields
    }
  }
  ${SERVICE_SITE_FIELDS}
`;

export const UPDATE_SERVICE_SITE = gql`
  mutation UpdateServiceSite($siteId: ID!, $input: ServiceSiteInput!) {
    updateServiceSite(siteId: $siteId, input: $input) {
      ...ServiceSiteFields
    }
  }
  ${SERVICE_SITE_FIELDS}
`;

export const DEACTIVATE_SERVICE_SITE = gql`
  mutation DeactivateServiceSite($siteId: ID!) {
    deactivateServiceSite(siteId: $siteId) {
      ...ServiceSiteFields
    }
  }
  ${SERVICE_SITE_FIELDS}
`;

/** A job as its customer reads it. */
export const SERVICE_JOB_BOOKING_FIELDS = gql`
  fragment ServiceJobBookingFields on Booking {
    id
    status
    guestDisplayName
    productType
    createdAt
    startAt
    endAt
    cancellationReason
    car {
      id
      name
      thumbnailUrl
      images {
        imageUrl
      }
      serviceWorker {
        carId
        displayName
        city
        zip
        equipmentPhotos
        averageRating
        totalRatings
        onTimeRate
        completedJobCount
        backgroundCheckVerified
        provider {
          userId
          businessName
          insuranceVerified
        }
      }
    }
    receipt {
      totalPrice
      serviceSubtotal
      addOnsSubtotal
      travelFee
      platformServiceFee
      serviceTax
      authorizedAmount
      addOnAllowance
      handoverMinutes
      finalTotal
    }
    serviceJob {
      site {
        kind
        nickname
        city
        zip
        addressLine1
        addressLine2
        homeDetails
        vehicleDetails
      }
      serviceDate
      windowStartAt
      windowEndAt
      timezone
      items
      selectedAddOns
      jobStatus
      maxBillableMinutes
      billedHandoverMinutes
      fromCalendar
      customerNote
      completedAt
      confirmedAt
      refundWindowEndsAt
      authorizeAfter
      respondBy
      workerName
    }
  }
`;

export const CREATE_JOB_INTENT = gql`
  mutation CreateJobIntent($input: CreateJobInput!) {
    createJobIntent(input: $input) {
      requiresConfirmation
      authorizeNow
      booking {
        ...ServiceJobBookingFields
      }
    }
  }
  ${SERVICE_JOB_BOOKING_FIELDS}
`;

export const SYNC_SERVICE_JOB_PAYMENT = gql`
  mutation SyncServiceJobPayment($bookingId: ID!) {
    syncServiceJobPayment(bookingId: $bookingId) {
      ...ServiceJobBookingFields
    }
  }
  ${SERVICE_JOB_BOOKING_FIELDS}
`;

export const SERVICE_JOB_AS_CUSTOMER = gql`
  query ServiceJobAsCustomer($id: String!) {
    getBookingAsGuest(id: $id) {
      ...ServiceJobBookingFields
    }
  }
  ${SERVICE_JOB_BOOKING_FIELDS}
`;

/** Every job of the customer on this brand except unpaid drafts (spec 019 W16). */
export const MY_SERVICE_JOBS = gql`
  query MyServiceJobs($limit: Int) {
    myServiceJobs(limit: $limit) {
      ...ServiceJobBookingFields
    }
  }
  ${SERVICE_JOB_BOOKING_FIELDS}
`;

export const JOB_CANCELLATION_PREVIEW = gql`
  query JobCancellationPreview($bookingId: ID!) {
    jobCancellationPreview(bookingId: $bookingId) {
      fee
      refund
      countsAsLateCancel
      tier
      message
    }
  }
`;

export const CANCEL_JOB = gql`
  mutation CancelJob($bookingId: ID!, $reason: String) {
    cancelJob(bookingId: $bookingId, reason: $reason) {
      ...ServiceJobBookingFields
    }
  }
  ${SERVICE_JOB_BOOKING_FIELDS}
`;

export const WITHDRAW_JOB = gql`
  mutation WithdrawJob($bookingId: ID!) {
    withdrawJob(bookingId: $bookingId) {
      ...ServiceJobBookingFields
    }
  }
  ${SERVICE_JOB_BOOKING_FIELDS}
`;

export const UPDATE_JOB_ACCESS = gql`
  mutation UpdateJobAccess($bookingId: ID!, $accessOverride: String) {
    updateJobAccess(bookingId: $bookingId, accessOverride: $accessOverride) {
      id
      status
    }
  }
`;

// ─── doing the job and after (spec 019 US3, W17 to W23) ────────────────────

export const PROVIDER_TODAY = gql`
  query ProviderToday($date: String) {
    providerToday(date: $date) {
      requests {
        ...ServiceJobBookingFields
      }
      today {
        ...ServiceJobBookingFields
      }
      inProgress {
        ...ServiceJobBookingFields
      }
      awaitingConfirmation {
        ...ServiceJobBookingFields
      }
      upcoming {
        ...ServiceJobBookingFields
      }
    }
  }
  ${SERVICE_JOB_BOOKING_FIELDS}
`;

export const SERVICE_JOB_AS_PROVIDER = gql`
  query ServiceJobAsProvider($id: String!) {
    getBookingAsHost(id: $id) {
      ...ServiceJobBookingFields
    }
  }
  ${SERVICE_JOB_BOOKING_FIELDS}
`;

export const ACCEPT_JOB = gql`
  mutation AcceptJob($bookingId: ID!) {
    acceptJob(bookingId: $bookingId) {
      ...ServiceJobBookingFields
    }
  }
  ${SERVICE_JOB_BOOKING_FIELDS}
`;

export const DECLINE_JOB = gql`
  mutation DeclineJob($bookingId: ID!, $reason: String) {
    declineJob(bookingId: $bookingId, reason: $reason) {
      ...ServiceJobBookingFields
    }
  }
  ${SERVICE_JOB_BOOKING_FIELDS}
`;

export const MY_WORKER_JOBS = gql`
  query MyWorkerJobs($date: String) {
    myWorkerJobs(date: $date) {
      ...ServiceJobBookingFields
    }
  }
  ${SERVICE_JOB_BOOKING_FIELDS}
`;

export const JOB_TIMELINE_FIELDS = gql`
  fragment JobTimelineFields on JobTimeline {
    bookingId
    myRole
    jobStatus
    checklist
    handoverMinutesSoFar
    customerNote
    events {
      id
      type
      actorRole
      capturedAt
      receivedAt
      locationMismatch
      checklistItemCode
      photoUrls
      payload
      voidedAt
    }
    addOns {
      id
      code
      label
      quantity
      amount
      status
      photoUrls
      receiptUrl
      respondBy
      clientSecret
    }
  }
`;

export const JOB_TIMELINE = gql`
  query JobTimeline($bookingId: ID!) {
    jobTimeline(bookingId: $bookingId) {
      ...JobTimelineFields
    }
  }
  ${JOB_TIMELINE_FIELDS}
`;

export const JOB_ACCESS = gql`
  query JobAccess($bookingId: ID!) {
    jobAccess(bookingId: $bookingId) {
      address
      accessInstructions
      availableAt
    }
  }
`;

export const RECORD_JOB_EVENT = gql`
  mutation RecordJobEvent($input: RecordJobEventInput!) {
    recordJobEvent(input: $input) {
      ...JobTimelineFields
    }
  }
  ${JOB_TIMELINE_FIELDS}
`;

export const GET_PRESIGNED_URL_FOR_JOB_PHOTO = gql`
  mutation GetPresignedUrlForJobPhoto(
    $bookingId: ID!
    $contentType: String!
    $contentLength: Int!
    $receipt: Boolean
  ) {
    getPresignedUrlForJobPhoto(
      bookingId: $bookingId
      contentType: $contentType
      contentLength: $contentLength
      receipt: $receipt
    ) {
      url
      key
    }
  }
`;

export const REQUEST_JOB_PHOTO_ARCHIVE = gql`
  mutation RequestJobPhotoArchive($bookingId: ID!) {
    requestJobPhotoArchive(bookingId: $bookingId)
  }
`;

export const RESPOND_JOB_ADD_ON = gql`
  mutation RespondJobAddOn($addOnId: ID!, $approve: Boolean!) {
    respondJobAddOn(addOnId: $addOnId, approve: $approve) {
      id
      status
      clientSecret
    }
  }
`;

export const CONFIRM_JOB_COMPLETION = gql`
  mutation ConfirmJobCompletion($bookingId: ID!) {
    confirmJobCompletion(bookingId: $bookingId) {
      ...ServiceJobBookingFields
    }
  }
  ${SERVICE_JOB_BOOKING_FIELDS}
`;

export const DISPUTE_JOB = gql`
  mutation DisputeJob($bookingId: ID!, $note: String!, $photoKeys: [String!]) {
    disputeJob(bookingId: $bookingId, note: $note, photoKeys: $photoKeys) {
      ...ServiceJobBookingFields
    }
  }
  ${SERVICE_JOB_BOOKING_FIELDS}
`;

export const REQUEST_JOB_REFUND = gql`
  mutation RequestJobRefund(
    $bookingId: ID!
    $amount: Float!
    $note: String!
    $photoKeys: [String!]
  ) {
    requestJobRefund(bookingId: $bookingId, amount: $amount, note: $note, photoKeys: $photoKeys) {
      id
      status
    }
  }
`;

export const EXTEND_HANDOVER_CAP = gql`
  mutation ExtendHandoverCap($bookingId: ID!, $maxBillableMinutes: Int!) {
    extendHandoverCap(bookingId: $bookingId, maxBillableMinutes: $maxBillableMinutes) {
      status
      clientSecret
      paymentIntentId
      errorMessage
      booking {
        ...ServiceJobBookingFields
      }
    }
  }
  ${SERVICE_JOB_BOOKING_FIELDS}
`;

export const CONFIRM_HANDOVER_CAP_EXTENSION = gql`
  mutation ConfirmHandoverCapExtension($bookingId: ID!, $paymentIntentId: ID!) {
    confirmHandoverCapExtension(bookingId: $bookingId, paymentIntentId: $paymentIntentId) {
      ...ServiceJobBookingFields
    }
  }
  ${SERVICE_JOB_BOOKING_FIELDS}
`;

export const ADMIN_SERVICE_DISPUTES = gql`
  query AdminServiceDisputes($status: String) {
    adminServiceDisputes(status: $status) {
      ...ServiceJobBookingFields
    }
  }
  ${SERVICE_JOB_BOOKING_FIELDS}
`;

export const RESOLVE_JOB_DISPUTE = gql`
  mutation ResolveJobDispute($bookingId: ID!, $refundAmount: Float!, $note: String!) {
    resolveJobDispute(bookingId: $bookingId, refundAmount: $refundAmount, note: $note) {
      ...ServiceJobBookingFields
    }
  }
  ${SERVICE_JOB_BOOKING_FIELDS}
`;

export const ADMIN_WORKER_STRIKES = gql`
  query AdminWorkerStrikes($pendingAppealOnly: Boolean) {
    adminWorkerStrikes(pendingAppealOnly: $pendingAppealOnly) {
      id
      type
      actorRole
      capturedAt
      receivedAt
      payload
      voidedAt
    }
  }
`;

/** One worker's strikes of the last 90 days, for its Provider (D17). */
export const WORKER_STRIKES = gql`
  query WorkerStrikes($workerCarId: ID!) {
    workerStrikes(workerCarId: $workerCarId) {
      id
      type
      actorRole
      capturedAt
      receivedAt
      payload
      voidedAt
    }
  }
`;

export const RESOLVE_WORKER_STRIKE = gql`
  mutation ResolveWorkerStrike($eventId: ID!, $upheld: Boolean!) {
    resolveWorkerStrike(eventId: $eventId, upheld: $upheld) {
      id
      voidedAt
      payload
    }
  }
`;

export const RESUME_WORKER = gql`
  mutation ResumeWorker($workerCarId: ID!) {
    resumeWorker(workerCarId: $workerCarId) {
      id
      status
    }
  }
`;

export const APPEAL_WORKER_STRIKE = gql`
  mutation AppealWorkerStrike($eventId: ID!, $note: String!, $fileKey: String) {
    appealWorkerStrike(eventId: $eventId, note: $note, fileKey: $fileKey) {
      id
      payload
      voidedAt
    }
  }
`;

export const SERVICE_TAX_RULES = gql`
  query ServiceTaxRules {
    serviceTaxRules {
      state
      serviceType
      taxable
      platformFeeTaxable
      rate
    }
  }
`;

export const SET_SERVICE_TAX_RULE = gql`
  mutation SetServiceTaxRule(
    $state: String!
    $serviceType: ServiceType!
    $taxable: Boolean!
    $platformFeeTaxable: Boolean!
    $rate: Float
  ) {
    setServiceTaxRule(
      state: $state
      serviceType: $serviceType
      taxable: $taxable
      platformFeeTaxable: $platformFeeTaxable
      rate: $rate
    ) {
      state
      serviceType
      taxable
      platformFeeTaxable
      rate
    }
  }
`;

// ─── reviews, payouts (spec 019 R16, D23) ──────────────────────────────────

/** Each side sees only its own review until both are in or 14 days pass. */
export const SERVICE_JOB_RATING = gql`
  query ServiceJobRating($bookingId: ID!) {
    getTripRating(bookingId: $bookingId) {
      id
      guestRatedAt
      hostRatedAt
      serviceCustomerRating {
        overall
        thoroughness
        punctuality
        communication
        photoCompleteness
        review
      }
      serviceWorkerRating {
        overall
        accuracy
        access
        communication
        review
      }
    }
  }
`;

export const SUBMIT_SERVICE_CUSTOMER_RATING = gql`
  mutation SubmitServiceCustomerRating($input: ServiceCustomerRatingInput!) {
    submitServiceCustomerRating(input: $input) {
      id
      guestRatedAt
    }
  }
`;

export const SUBMIT_SERVICE_WORKER_RATING = gql`
  mutation SubmitServiceWorkerRating($input: ServiceWorkerRatingInput!) {
    submitServiceWorkerRating(input: $input) {
      id
      hostRatedAt
    }
  }
`;

export const SERVICE_PAYOUT_BATCH_FIELDS = gql`
  fragment ServicePayoutBatchFields on ServicePayoutBatch {
    id
    providerId
    providerName
    periodStart
    periodEnd
    amount
    bookingIds
    jobCount
    paidAt
    reference
  }
`;

export const MY_SERVICE_PAYOUTS = gql`
  query MyServicePayouts {
    myServicePayouts {
      pendingAmount
      pendingJobCount
      batches {
        ...ServicePayoutBatchFields
      }
    }
  }
  ${SERVICE_PAYOUT_BATCH_FIELDS}
`;

export const ADMIN_SERVICE_PAYOUT_BATCHES = gql`
  query AdminServicePayoutBatches($periodStart: String) {
    adminServicePayoutBatches(periodStart: $periodStart) {
      ...ServicePayoutBatchFields
    }
  }
  ${SERVICE_PAYOUT_BATCH_FIELDS}
`;

export const SERVICE_PAYOUT_BATCHES_CSV = gql`
  query ServicePayoutBatchesCsv($periodStart: String!) {
    servicePayoutBatchesCsv(periodStart: $periodStart)
  }
`;

export const MARK_PAYOUT_BATCH_PAID = gql`
  mutation MarkPayoutBatchPaid($batchId: ID!, $reference: String!) {
    markPayoutBatchPaid(batchId: $batchId, reference: $reference) {
      ...ServicePayoutBatchFields
    }
  }
  ${SERVICE_PAYOUT_BATCH_FIELDS}
`;

/** D28: platform owner; a signed CSV link of last year's totals and tax info. */
export const PROVIDER_TAX_EXPORT = gql`
  query ProviderTaxExport($year: Int!) {
    providerTaxExport(year: $year)
  }
`;

/** Customer reviews of one worker that show (both sides in, or 14 days past). */
export const WORKER_REVIEWS = gql`
  query WorkerReviews($carId: ID!, $limit: Int, $offset: Int) {
    getCarRatings(carId: $carId, limit: $limit, offset: $offset) {
      totalCount
      summary {
        averageRating
        totalRatings
        ratingDistribution {
          fiveStars
          fourStars
          threeStars
          twoStars
          oneStar
        }
      }
      reviews {
        guestName
        tripDate
        rating {
          id
          serviceCustomerRating {
            overall
            thoroughness
            punctuality
            communication
            photoCompleteness
            review
          }
        }
      }
    }
  }
`;

// ─── minimum wages (D26, platform owner) ───────────────────────────────────

export const MINIMUM_WAGE_RATES = gql`
  query MinimumWageRates($state: String) {
    minimumWageRates(state: $state) {
      id
      state
      city
      hourlyRate
      effectiveFrom
      source
    }
  }
`;

export const UPSERT_MINIMUM_WAGE_RATE = gql`
  mutation UpsertMinimumWageRate(
    $state: String!
    $hourlyRate: Float!
    $effectiveFrom: String!
    $source: String!
    $city: String
  ) {
    upsertMinimumWageRate(
      state: $state
      hourlyRate: $hourlyRate
      effectiveFrom: $effectiveFrom
      source: $source
      city: $city
    ) {
      id
      state
      city
      hourlyRate
      effectiveFrom
      source
    }
  }
`;

/** D19, T070: read the home's Airbnb or Vrbo calendar now. */
export const SYNC_SERVICE_SITE_CALENDAR = gql`
  mutation SyncServiceSiteCalendar($siteId: ID!) {
    syncServiceSiteCalendar(siteId: $siteId) {
      ...ServiceSiteFields
    }
  }
  ${SERVICE_SITE_FIELDS}
`;
